import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ProtectedComponent } from '../../../domain/components/rbac/ProtectedComponent';
import { Role } from '../../../domain/types/rbac';
import { User } from '../../../domain/types/user';
import { AdminUserService } from '../../../domain/use-cases/admin-user/admin-user-service';
import { RoleService } from '../../../domain/use-cases/rbac/role-service';
import Button from '../../components/button';
import Spinner from '../../components/spinner';
import Table from '../../components/table';

export function AdminUsersPage() {
  const [adminUsers, setAdminUsers] = useState<User[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const navigate = useNavigate();

  // Instanciar servicios fuera del useCallback para evitar dependencias
  const adminUserService = React.useMemo(() => new AdminUserService(), []);
  const roleService = React.useMemo(() => new RoleService(), []);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Cargar usuarios admin y roles en paralelo
      const [usersResult, rolesData] = await Promise.all([
        adminUserService.getAllAdminUsers(),
        roleService.getAllRoles(),
      ]);

      if (usersResult.success) {
        setAdminUsers(usersResult.data || []);
      } else {
        setError(usersResult.message);
      }

      setRoles(rolesData || []);
    } catch (err) {
      console.error('Error loading admin users:', err);
      setError('Error al cargar los usuarios administrativos');
    } finally {
      setLoading(false);
    }
  }, [adminUserService, roleService]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleDelete = async (userId: string, userEmail: string) => {
    if (
      !window.confirm(
        `¿Estás seguro de que deseas eliminar al usuario ${userEmail}?`,
      )
    ) {
      return;
    }

    try {
      const result = await adminUserService.deleteAdminUser(userId);
      if (result.success) {
        toast.success('Usuario eliminado correctamente', { theme: 'colored' });
        await loadData(); // Recargar datos
      } else {
        toast.error(`Error al eliminar usuario: ${result.message}`, {
          theme: 'colored',
        });
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Error al eliminar el usuario', { theme: 'colored' });
    }
  };

  const getRoleNames = (userRoles: string[]) => {
    if (!userRoles || userRoles.length === 0) return 'Sin roles';

    return userRoles
      .map((roleId) => {
        const role = roles.find((r) => r.id === roleId);
        return role ? role.name : roleId;
      })
      .join(', ');
  };

  const columns = [
    {
      header: 'Email',
      accessor: 'email' as keyof User,
      render: (_: any, user: User) => (
        <div className="font-medium text-gray-900">{user.email}</div>
      ),
    },
    {
      header: 'Nombre',
      accessor: 'name' as keyof User,
      render: (_: any, user: User) => (
        <div>
          <div className="font-medium text-gray-900">
            {user.name} {user.lastname}
          </div>
        </div>
      ),
    },
    {
      header: 'Roles',
      accessor: 'roles' as keyof User,
      render: (_: any, user: User) => (
        <div className="text-sm">{getRoleNames(user.roles || [])}</div>
      ),
    },
    {
      header: 'Estado',
      accessor: 'isEnabled' as keyof User,
      render: (_: any, user: User) => (
        <span
          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
            user.isEnabled !== false
              ? 'bg-green-100 text-green-800'
              : 'bg-red-100 text-red-800'
          }`}
        >
          {user.isEnabled !== false ? 'Activo' : 'Inactivo'}
        </span>
      ),
    },
    {
      header: 'Acciones',
      accessor: 'id' as keyof User,
      render: (_: any, user: User) => (
        <div className="flex space-x-2">
          <Button
            label=""
            variant="secondary"
            onClick={() => navigate(`/dashboard/admin-users/${user.id}`)}
            className="p-2 text-xs"
            icon={
              <svg
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                />
              </svg>
            }
          />
          <Button
            label=""
            variant="danger"
            onClick={() => handleDelete(user.id, user.email)}
            className="p-2 text-xs"
            icon={
              <svg
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
            }
          />
        </div>
      ),
    },
  ];

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-96">
        <Spinner />
      </div>
    );
  }

  return (
    <ProtectedComponent
      resource="admin_users"
      action="read"
      fallback={
        <div className="flex justify-center items-center min-h-96">
          <div className="text-center">
            <h2 className="text-lg font-medium text-gray-900">
              Acceso Denegado
            </h2>
            <p className="text-gray-600 mt-1">
              No tienes permisos para ver esta página
            </p>
          </div>
        </div>
      }
    >
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Usuarios Administrativos
            </h1>
            <p className="text-gray-600 mt-1">
              Gestiona los usuarios que tienen acceso al panel de administración
            </p>
          </div>
          <Button
            label="Agregar Usuario"
            onClick={() => navigate('/dashboard/admin-users/add')}
            className="flex items-center space-x-2"
            icon={
              <svg
                className="h-5 w-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                />
              </svg>
            }
            positionIcon="left"
          />
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-blue-100">
                <svg
                  className="h-8 w-8 text-blue-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"
                  />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">
                  Total Usuarios
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {adminUsers.length}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-green-100">
                <svg
                  className="h-8 w-8 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">
                  Usuarios Activos
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {adminUsers.filter((user) => user.isEnabled !== false).length}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-purple-100">
                <svg
                  className="h-8 w-8 text-purple-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">
                  Roles Disponibles
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {roles.length}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error</h3>
                <div className="mt-2 text-sm text-red-700">{error}</div>
              </div>
            </div>
          </div>
        )}

        {/* Users Table */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">
              Lista de Usuarios Administrativos
            </h3>
          </div>
          <Table data={adminUsers} columns={columns} />
        </div>
      </div>
    </ProtectedComponent>
  );
}
